// EmailTemplatesScreen.js — JOBENG Full-Feature Email Templates
// Features: Full CRUD | AI generation | Variable substitution | Preview | Usage analytics
// Bulk send | Campaign mode | Categories | Favourite | Version history | Compose integration

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
   View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, Alert, Modal, TextInput, ActivityIndicator, FlatList,
  Platform, KeyboardAvoidingView, Share, Animated, SectionList, Switch, Image,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as DocumentPicker from 'expo-document-picker';
import axios from 'axios';
import { useCompanyLogo } from '../../../hooks/useCompanyLogo';
import SafeAreaView from 'react-native-safe-area-context';
const { width, height } = Dimensions.get('window');

// ─── Brand ───────────────────────────────────────────────────────────────────
const BRAND = {
  primary: '#0e4e96', teal: '#19abb5', gold: '#fdb913',
  success: '#10b981', warning: '#f59e0b', danger: '#ef4444',
  purple: '#8b5cf6', blue: '#3b82f6', orange: '#f97316',
  dark: '#0f172a', sub: '#475569', line: '#e2e8f0',
  bg: '#f8fafc', white: '#ffffff',
};

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // Emails
  emails:             `${API_BASE_URL}/api/emails`,
  emailSend:          `${API_BASE_URL}/api/emails/send`,
  // Templates
  templates:          `${API_BASE_URL}/api/email-templates`,
  templateById:       (id) => `${API_BASE_URL}/api/email-templates/${id}`,
  templateDuplicate:  (id) => `${API_BASE_URL}/api/email-templates/${id}/duplicate`,
  templateFavourite:  (id) => `${API_BASE_URL}/api/email-templates/${id}/favourite`,
  // Contacts
  contacts:           `${API_BASE_URL}/api/email-contacts`,
  contactById:        (id) => `${API_BASE_URL}/api/email-contacts/${id}`,
  // Analytics
  analytics:          `${API_BASE_URL}/api/email-analytics`,
  analyticsDashboard: `${API_BASE_URL}/api/email-analytics/dashboard`,
  // Profile
  profile:            `${API_BASE_URL}/api/data/company_profiles`,
};
const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (_) {}
  return config;
});

// ─── Constants ───────────────────────────────────────────────────────────────
const CATEGORIES = [
  { key: 'all',           label: 'All',              icon: 'view-grid',         color: BRAND.sub },
  { key: 'quotation',     label: 'Quotation',        icon: 'currency-gbp',      color: BRAND.blue },
  { key: 'tender',        label: 'Tender',           icon: 'file-document',     color: BRAND.purple },
  { key: 'followup',      label: 'Follow-up',        icon: 'reply',             color: BRAND.warning },
  { key: 'meeting',       label: 'Meeting',          icon: 'calendar',          color: BRAND.teal },
  { key: 'invoice',       label: 'Invoice',          icon: 'receipt',           color: BRAND.success },
  { key: 'hr',            label: 'HR',               icon: 'account-group',     color: BRAND.orange },
  { key: 'support',       label: 'Support',          icon: 'lifebuoy',          color: BRAND.danger },
  { key: 'marketing',     label: 'Marketing',        icon: 'bullhorn',          color: BRAND.primary },
  { key: 'general',       label: 'General',          icon: 'email',             color: BRAND.sub },
];

const DEFAULT_VARIABLES = [
  '[Client Name]', '[Project Name]', '[Date]', '[Your Name]', '[Your Title]',
  '[Company]', '[Amount]', '[Invoice No]', '[Location]', '[Time]',
  '[Phone]', '[Email]', '[Reference]', '[Deadline]', '[URL]',
];

const RECIPIENTS_SAMPLE = [
  { id: 'r1', name: 'John Smith',    email: 'john.smith@abcconstruction.com',  company: 'ABC Construction',      group: 'Clients' },
  { id: 'r2', name: 'Sarah Johnson', email: 'sarah@greenenergy.com',            company: 'Green Energy Solutions', group: 'Clients' },
  { id: 'r3', name: 'Mike Wilson',   email: 'mike@moderninteriors.co.uk',       company: 'Modern Interiors',       group: 'Clients' },
  { id: 'r4', name: 'City Council',  email: 'procurement@citycouncil.gov.uk',   company: 'City Council',           group: 'Clients' },
  { id: 'r5', name: 'Alice Brown',   email: 'alice.brown@jobeng.ai',            company: 'JOBENG',                 group: 'Team' },
  { id: 'r6', name: 'David Lee',     email: 'david.lee@jobeng.ai',              company: 'JOBENG',                 group: 'Team' },
  { id: 'r7', name: 'Emily Davis',   email: 'emily.davis@candidate.com',        company: 'N/A',                    group: 'Candidates' },
  { id: 'r8', name: 'Tom Clarke',    email: 'tom.clarke@candidate.com',         company: 'N/A',                    group: 'Candidates' },
];

const INITIAL_TEMPLATES = [
  {
    id: 'tpl001', name: 'Project Proposal', category: 'quotation',
    subject: 'Project Proposal: [Project Name]',
    body: 'Dear [Client Name],\n\nThank you for the opportunity to submit our proposal for [Project Name].\n\nFollowing our review of the brief, we are pleased to propose the following approach:\n\n1. Project Understanding\nBased on the information provided, we understand the key deliverables to include [Project Details].\n\n2. Our Approach\n[Methodology]\n\n3. Programme\nWe anticipate a delivery programme of [Timeline], subject to agreement.\n\n4. Fee Proposal\nOur proposed professional fee for the above scope is [Amount] + VAT.\n\nWe would be delighted to discuss this further at your convenience.\n\nBest regards,\n[Your Name]\n[Your Title]\nJOBENG',
    variables: ['Client Name', 'Project Name', 'Project Details', 'Methodology', 'Timeline', 'Amount', 'Your Name', 'Your Title'],
    tags: ['proposal', 'client', 'fee', 'engineering'],
    usageCount: 24, lastUsed: '2026-03-01', favourite: true, pinned: true,
    versions: [{ version: '1.0', date: '2025-01-10', note: 'Initial version' }, { version: '1.1', date: '2025-09-15', note: 'Updated fee structure' }],
    createdAt: '2025-01-10', updatedAt: '2026-03-01',
  },
  {
    id: 'tpl002', name: 'Tender Response', category: 'tender',
    subject: 'Tender Submission: [Reference]',
    body: 'Dear [Procurement Team],\n\nPlease find enclosed our tender submission for [Reference].\n\nAs required, we have provided the following documents:\n1. Completed Questionnaire\n2. Method Statement\n3. Fee Schedule\n4. CVs of Key Personnel\n5. Company Profile & Insurance Certificates\n\nOur submission demonstrates our extensive experience delivering [Service Type] projects, with a proven track record across the [Sector] sector.\n\nWe trust our submission meets all requirements and look forward to the opportunity to discuss further.\n\nYours faithfully,\n[Your Name]\n[Your Title]\nJOBENG',
    variables: ['Procurement Team', 'Reference', 'Service Type', 'Sector', 'Your Name', 'Your Title'],
    tags: ['tender', 'procurement', 'formal'],
    usageCount: 18, lastUsed: '2026-02-28', favourite: true, pinned: true,
    versions: [{ version: '1.0', date: '2025-03-01', note: 'Initial' }],
    createdAt: '2025-03-01', updatedAt: '2026-02-28',
  },
  {
    id: 'tpl003', name: 'Meeting Follow-Up', category: 'followup',
    subject: 'Meeting Follow-Up: [Subject] – [Date]',
    body: 'Dear [Name],\n\nThank you for meeting with us on [Date] to discuss [Subject].\n\nAs agreed, the key actions are as follows:\n\nAction Points:\n1. [Action 1] – Owner: [Owner 1] – Due: [Due Date 1]\n2. [Action 2] – Owner: [Owner 2] – Due: [Due Date 2]\n3. [Action 3] – Owner: [Owner 3] – Due: [Due Date 3]\n\nNext Steps:\n[Next Steps]\n\nProposed Next Meeting: [Next Meeting Date]\n\nPlease confirm the above are correct or let me know if anything needs amending.\n\nKind regards,\n[Your Name]',
    variables: ['Name', 'Date', 'Subject', 'Action 1', 'Owner 1', 'Due Date 1', 'Action 2', 'Owner 2', 'Due Date 2', 'Action 3', 'Owner 3', 'Due Date 3', 'Next Steps', 'Next Meeting Date', 'Your Name'],
    tags: ['meeting', 'follow-up', 'actions', 'minutes'],
    usageCount: 31, lastUsed: '2026-03-05', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-02-01', note: 'Initial' }],
    createdAt: '2025-02-01', updatedAt: '2026-03-05',
  },
  {
    id: 'tpl004', name: 'Interview Invitation', category: 'hr',
    subject: 'Interview Invitation – [Position] at JOBENG',
    body: 'Dear [Candidate Name],\n\nThank you for your application for the [Position] role at JOBENG.\n\nHaving reviewed your application carefully, we are pleased to invite you to the next stage of our recruitment process.\n\nInterview Details:\n• Date: [Date]\n• Time: [Time]\n• Duration: Approximately [Duration]\n• Format: [Format – e.g. face-to-face / video call]\n• Location / Link: [Location]\n• Interviewers: [Interviewers]\n\nPlease could you confirm your attendance by return email. If the proposed time is not convenient, please suggest an alternative and we will do our best to accommodate.\n\nIf you have any questions prior to the interview, please do not hesitate to contact us.\n\nWe look forward to meeting you.\n\nBest regards,\n[Your Name]\nHR Department\nJOBENG',
    variables: ['Candidate Name', 'Position', 'Date', 'Time', 'Duration', 'Format', 'Location', 'Interviewers', 'Your Name'],
    tags: ['recruitment', 'interview', 'hr', 'candidate'],
    usageCount: 12, lastUsed: '2026-02-20', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-01-15', note: 'Initial' }],
    createdAt: '2025-01-15', updatedAt: '2026-02-20',
  },
  {
    id: 'tpl005', name: 'Invoice Payment Reminder', category: 'invoice',
    subject: 'Payment Reminder – Invoice [Invoice No] – [Amount] Due',
    body: 'Dear [Client Name],\n\nI hope this message finds you well.\n\nThis is a friendly reminder that Invoice [Invoice No] for [Amount] was due for payment on [Due Date] and remains outstanding.\n\nInvoice Summary:\n• Invoice Number: [Invoice No]\n• Invoice Date: [Invoice Date]\n• Amount Due: [Amount]\n• Payment Due Date: [Due Date]\n• Description: [Description]\n\nTo settle this invoice, please transfer payment to:\nBank: [Bank]\nSort Code: [Sort Code]\nAccount Number: [Account No]\nPayment Reference: [Invoice No]\n\nIf payment has already been made, please disregard this notice and accept our thanks.\n\nShould you wish to discuss payment arrangements, please do not hesitate to contact us.\n\nKind regards,\n[Your Name]\nJOBENG Finance Department',
    variables: ['Client Name', 'Invoice No', 'Amount', 'Due Date', 'Invoice Date', 'Description', 'Bank', 'Sort Code', 'Account No', 'Your Name'],
    tags: ['invoice', 'payment', 'finance', 'reminder'],
    usageCount: 9, lastUsed: '2026-02-25', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-04-01', note: 'Initial' }],
    createdAt: '2025-04-01', updatedAt: '2026-02-25',
  },
  {
    id: 'tpl006', name: 'Service Announcement', category: 'marketing',
    subject: 'Introducing [Service Name] from JOBENG',
    body: 'Dear [Client Name],\n\nWe are excited to announce the launch of our new [Service Name] service.\n\n[Service Description]\n\nKey Benefits:\n• [Benefit 1]\n• [Benefit 2]\n• [Benefit 3]\n\nThis service is now available to all clients and we would welcome the opportunity to discuss how it can benefit your organisation.\n\nFor more information or to arrange a demonstration, please contact us at [Phone] or [Email].\n\nBest regards,\n[Your Name]\n[Your Title]\nJOBENG',
    variables: ['Client Name', 'Service Name', 'Service Description', 'Benefit 1', 'Benefit 2', 'Benefit 3', 'Phone', 'Email', 'Your Name', 'Your Title'],
    tags: ['marketing', 'announcement', 'service', 'newsletter'],
    usageCount: 6, lastUsed: '2026-01-30', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-06-01', note: 'Initial' }],
    createdAt: '2025-06-01', updatedAt: '2026-01-30',
  },
  {
    id: 'tpl007', name: 'Project Completion Notice', category: 'general',
    subject: 'Project Completion – [Project Name]',
    body: 'Dear [Client Name],\n\nWe are pleased to confirm the successful completion of [Project Name].\n\nAll deliverables have been provided in accordance with the agreed scope and programme.\n\nCompletion Summary:\n• Project: [Project Name]\n• Completion Date: [Date]\n• Reference: [Reference]\n\nPlease find attached our final completion report for your records.\n\nIt has been a pleasure working with you on this project, and we look forward to supporting you on future commissions.\n\nBest regards,\n[Your Name]\n[Your Title]\nJOBENG',
    variables: ['Client Name', 'Project Name', 'Date', 'Reference', 'Your Name', 'Your Title'],
    tags: ['completion', 'handover', 'close-out'],
    usageCount: 7, lastUsed: '2026-01-15', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-05-20', note: 'Initial' }],
    createdAt: '2025-05-20', updatedAt: '2026-01-15',
  },
  {
    id: 'tpl008', name: 'Team Meeting Agenda', category: 'meeting',
    subject: 'Team Meeting Agenda – [Date]',
    body: 'Dear Team,\n\nPlease find below the agenda for our team meeting on [Date] at [Time].\n\nVenue: [Location]\n\nAgenda:\n1. Welcome & Apologies – [Duration]\n2. Review of Previous Minutes – [Duration]\n3. [Item 1] – [Duration]\n4. [Item 2] – [Duration]\n5. [Item 3] – [Duration]\n6. AOB – [Duration]\n\nPlease come prepared to provide brief updates on your current projects and flag any issues or risks.\n\nMeeting papers to be distributed by [Papers Date].\n\nRegards,\n[Your Name]',
    variables: ['Date', 'Time', 'Location', 'Item 1', 'Item 2', 'Item 3', 'Duration', 'Papers Date', 'Your Name'],
    tags: ['meeting', 'agenda', 'internal', 'team'],
    usageCount: 15, lastUsed: '2026-03-04', favourite: false, pinned: false,
    versions: [{ version: '1.0', date: '2025-03-10', note: 'Initial' }],
    createdAt: '2025-03-10', updatedAt: '2026-03-04',
  },
];

// ─── AI Template Generator ────────────────────────────────────────────────────
const generateAITemplate = async (prompt, category) => {
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01',
        'x-api-key': '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',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 800,
        messages: [{
          role: 'user',
          content: `Create a professional email template for a UK engineering consultancy (JOBENG) for the following purpose: ${prompt}. Category: ${category}.\n\nReturn ONLY a JSON object with these exact fields:\n{\n  "name": "template name",\n  "subject": "email subject with [variables]",\n  "body": "email body with [variables] for customisation",\n  "variables": ["Variable1", "Variable2"],\n  "tags": ["tag1", "tag2"]\n}\n\nUse [Variable Name] format for placeholders. Be professional, concise and appropriate for UK business communication. No markdown backticks.`,
        }],
      }),
    });
    if (!response.ok) throw new Error('API error');
    const data = await response.json();
    const text = data.content?.[0]?.text || '';
    const clean = text.replace(/```json|```/g, '').trim();
    return JSON.parse(clean);
  } catch (_) {
    // Fallback template
    return {
      name: `${prompt.slice(0, 30)} Template`,
      subject: `[Subject] – ${prompt.slice(0, 20)}`,
      body: `Dear [Client Name],\n\nI am writing to [Purpose].\n\n[Details]\n\nPlease do not hesitate to contact me if you have any questions.\n\nBest regards,\n[Your Name]\nJOBENG`,
      variables: ['Client Name', 'Purpose', 'Details', 'Your Name'],
      tags: [category, 'generated'],
    };
  }
};

// Pure lookup helpers — stable module-level refs
const getCategoryColor = (key) => CATEGORIES.find(c => c.key === key)?.color || BRAND.sub;
const getCategoryIcon  = (key) => CATEGORIES.find(c => c.key === key)?.icon  || 'email';
const getCategoryLabel = (key) => CATEGORIES.find(c => c.key === key)?.label || key;


// ─── TemplateCard ─────────────────────────────────────────────────────────────
// Proper React.memo component — receives callbacks as props to avoid stale closures.
// Never re-renders unless its own tpl data or callbacks change.
const TemplateCard = React.memo(({ tpl, onUse, onDuplicate, onPreview, onDelete, onToggleFavourite, onEdit, onTogglePinned, onViewHistory }) => (
  <TouchableOpacity style={[styles.tplCard, tpl.pinned && styles.tplCardPinned]} onPress={() => onPreview(tpl)}>
    <View style={styles.tplCardTop}>
      <View style={[styles.catIconBadge, { backgroundColor: getCategoryColor(tpl.category) + '20' }]}>
        <MaterialCommunityIcons name={getCategoryIcon(tpl.category)} size={18} color={getCategoryColor(tpl.category)} />
      </View>
      <View style={{ flex: 1, marginLeft: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          {tpl.pinned && <MaterialCommunityIcons name="pin" size={13} color={BRAND.primary} />}
          <Text style={styles.tplCardName}>{tpl.name}</Text>
        </View>
        <Text style={styles.tplCardCat}>{getCategoryLabel(tpl.category)}</Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 4 }}>
        <TouchableOpacity onPress={() => onToggleFavourite(tpl.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <MaterialCommunityIcons name={tpl.favourite ? 'star' : 'star-outline'} size={20} color={tpl.favourite ? BRAND.gold : '#cbd5e1'} />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => Alert.alert(tpl.name, 'What would you like to do?', [
            { text: 'Edit',         onPress: () => onEdit(tpl) },
            { text: 'Duplicate',    onPress: () => onDuplicate(tpl) },
            { text: tpl.pinned ? 'Unpin' : 'Pin', onPress: () => onTogglePinned(tpl.id) },
            { text: 'View History', onPress: () => onViewHistory(tpl) },
            { text: 'Delete', style: 'destructive', onPress: () => onDelete(tpl.id) },
            { text: 'Cancel', style: 'cancel' },
          ])}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color={BRAND.sub} />
        </TouchableOpacity>
      </View>
    </View>

    <Text style={styles.tplCardSubject} numberOfLines={1}>{tpl.subject}</Text>
    <Text style={styles.tplCardPreview} numberOfLines={2}>{tpl.body}</Text>

    <View style={styles.tplCardFooter}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', flex: 1 }}>
        {tpl.tags?.slice(0, 3).map(tag => (
          <View key={tag} style={styles.tagChip}><Text style={styles.tagChipText}>#{tag}</Text></View>
        ))}
        {(tpl.variables?.length || 0) > 0 && (
          <View style={[styles.tagChip, { backgroundColor: BRAND.primary + '15' }]}>
            <Text style={[styles.tagChipText, { color: BRAND.primary }]}>{tpl.variables.length} vars</Text>
          </View>
        )}
      </View>
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
        <Text style={styles.tplMeta}>{tpl.usageCount} uses</Text>
        <Text style={styles.tplMeta}>{tpl.lastUsed}</Text>
      </View>
    </View>

    <View style={styles.tplActions}>
      <TouchableOpacity style={styles.tplActionBtn} onPress={() => onUse(tpl)}>
        <MaterialCommunityIcons name="pencil" size={15} color={BRAND.primary} />
        <Text style={[styles.tplActionText, { color: BRAND.primary }]}>Use</Text>
      </TouchableOpacity>
      <View style={styles.tplActionDiv} />
      <TouchableOpacity style={styles.tplActionBtn} onPress={() => onDuplicate(tpl)}>
        <MaterialCommunityIcons name="content-duplicate" size={15} color={BRAND.teal} />
        <Text style={[styles.tplActionText, { color: BRAND.teal }]}>Duplicate</Text>
      </TouchableOpacity>
      <View style={styles.tplActionDiv} />
      <TouchableOpacity style={styles.tplActionBtn} onPress={() => onPreview(tpl)}>
        <MaterialCommunityIcons name="eye" size={15} color={BRAND.sub} />
        <Text style={styles.tplActionText}>Preview</Text>
      </TouchableOpacity>
      <View style={styles.tplActionDiv} />
      <TouchableOpacity style={styles.tplActionBtn} onPress={() => onDelete(tpl.id)}>
        <MaterialCommunityIcons name="delete-outline" size={15} color={BRAND.danger} />
        <Text style={[styles.tplActionText, { color: BRAND.danger }]}>Delete</Text>
      </TouchableOpacity>
    </View>
  </TouchableOpacity>
));

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmailTemplatesScreen({ navigation, route }) {
  const { logo: companyLogo, companyName, initials: companyInitials } = useCompanyLogo();

  const { preselectedTemplate, preselectedRecipient } = route?.params || {};

  const [activeTab, setActiveTab]   = useState('templates'); // templates | compose | recipients | analytics
  const [templates, setTemplates]   = useState(INITIAL_TEMPLATES);
  const [contacts, setContacts]     = useState(RECIPIENTS_SAMPLE);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy]         = useState('recent');
  const [showFavourites, setShowFavourites] = useState(false);

  // Selected template / editing
  const [selectedTemplate, setSelectedTemplate] = useState(preselectedTemplate || null);
  const [editingTemplate, setEditingTemplate]   = useState(null);
  const [previewTemplate, setPreviewTemplate]   = useState(null);
  const [showVersions, setShowVersions]         = useState(false);

  // Compose state
  const [recipients, setRecipients] = useState(preselectedRecipient ? [preselectedRecipient] : []);
  const [emailSubject, setEmailSubject] = useState(preselectedTemplate?.subject || '');
  const [emailBody, setEmailBody]   = useState(preselectedTemplate?.body || '');
  const [emailPriority, setEmailPriority] = useState('normal');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [ccField, setCcField]       = useState('');
  const [bccField, setBccField]     = useState('');
  const [isSending, setIsSending]   = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  // Variable substitution
  const [varValues, setVarValues]   = useState({});
  const [showVarModal, setShowVarModal] = useState(false);
  const [currentVars, setCurrentVars]  = useState([]);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal]         = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showBulkSend, setShowBulkSend]       = useState(false);
  const [showSortFilter, setShowSortFilter]   = useState(false);
  const [aiPrompt, setAiPrompt]               = useState('');
  const [aiCategory, setAiCategory]           = useState('general');
  const [isAIGenerating, setIsAIGenerating]   = useState(false);

  // New template form
  const emptyForm = { name: '', category: 'general', subject: '', body: '', tags: '', variables: '' };
  const [templateForm, setTemplateForm] = useState(emptyForm);

  // Animations
  const headerAnim = useRef(new Animated.Value(0)).current;

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadTemplates();
    Animated.timing(headerAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, []);

  useEffect(() => {
    if (preselectedTemplate) {
      setSelectedTemplate(preselectedTemplate);
      setEmailSubject(preselectedTemplate.subject);
      setEmailBody(preselectedTemplate.body);
      setActiveTab('compose');
    }
  }, [preselectedTemplate]);

  const loadTemplates = async () => {
    try {
      // Serve cache immediately
      const cached = await AsyncStorage.getItem('email_templates_v2');
      if (cached) setTemplates(JSON.parse(cached));
      // Refresh from API
      const response = await api.get(API_ENDPOINTS.templates);
      if (response.data?.success && Array.isArray(response.data.data)) {
        setTemplates(response.data.data);
        await AsyncStorage.setItem('email_templates_v2', JSON.stringify(response.data.data));
      }
    } catch (_) {
      // Cache already served — fail silently
    }
  };

  const saveTemplates = async (updated) => {
    await AsyncStorage.setItem('email_templates_v2', JSON.stringify(updated));
    try {
      await api.post(API_ENDPOINTS.templates, { templates: updated });
    } catch (_) {}
  };

  // ─── Template CRUD ───────────────────────────────────────────────────────────
  const createTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      Alert.alert('Required Fields', 'Name, subject and body are required.'); return;
    }
    const vars = templateForm.variables
      ? templateForm.variables.split(',').map(v => v.trim()).filter(Boolean)
      : extractVariables(templateForm.body);
    const tags = templateForm.tags
      ? templateForm.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      : [];
    const newTpl = {
      id: editingTemplate?.id || `tpl_${Date.now()}`,
      name: templateForm.name.trim(),
      category: templateForm.category,
      subject: templateForm.subject.trim(),
      body: templateForm.body.trim(),
      variables: vars,
      tags,
      usageCount: editingTemplate?.usageCount || 0,
      lastUsed: editingTemplate?.lastUsed || 'Never',
      favourite: editingTemplate?.favourite || false,
      pinned: editingTemplate?.pinned || false,
      versions: editingTemplate ? [
        ...(editingTemplate.versions || []),
        { version: `1.${(editingTemplate.versions?.length || 0)}`, date: new Date().toISOString().split('T')[0], note: 'Updated' },
      ] : [{ version: '1.0', date: new Date().toISOString().split('T')[0], note: 'Created' }],
      createdAt: editingTemplate?.createdAt || new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
    };
    const updated = editingTemplate
      ? templates.map(t => t.id === editingTemplate.id ? newTpl : t)
      : [newTpl, ...templates];
    setTemplates(updated);
    await saveTemplates(updated);
    setShowCreateModal(false);
    setTemplateForm(emptyForm);
    setEditingTemplate(null);
    Alert.alert('✅ Saved', editingTemplate ? 'Template updated.' : 'New template created.');
  };

  const deleteTemplate = (id) => {
    Alert.alert('Delete Template', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        const updated = templates.filter(t => t.id !== id);
        setTemplates(updated);
        await saveTemplates(updated);
      }},
    ]);
  };

  const duplicateTemplate = async (tpl) => {
    const dup = {
      ...tpl, id: `tpl_${Date.now()}`,
      name: `${tpl.name} (Copy)`,
      usageCount: 0, lastUsed: 'Never', favourite: false, pinned: false,
      createdAt: new Date().toISOString().split('T')[0],
      versions: [{ version: '1.0', date: new Date().toISOString().split('T')[0], note: 'Duplicated' }],
    };
    const updated = [dup, ...templates];
    setTemplates(updated);
    await saveTemplates(updated);
    Alert.alert('Duplicated', `"${dup.name}" created.`);
  };

  const toggleFavourite = async (id) => {
    const updated = templates.map(t => t.id === id ? { ...t, favourite: !t.favourite } : t);
    setTemplates(updated);
    await saveTemplates(updated);
  };

  const togglePinned = async (id) => {
    const updated = templates.map(t => t.id === id ? { ...t, pinned: !t.pinned } : t);
    setTemplates(updated);
    await saveTemplates(updated);
  };

  // ─── Use Template ─────────────────────────────────────────────────────────────
  const useTemplate = async (tpl) => {
    // Extract variables from body
    const vars = extractVariables(tpl.subject + ' ' + tpl.body);
    setCurrentVars(vars);
    const initVals = {};
    vars.forEach(v => { initVals[v] = ''; });
    setVarValues(initVals);
    setSelectedTemplate(tpl);
    setEmailSubject(tpl.subject);
    setEmailBody(tpl.body);
    const updated = templates.map(t =>
      t.id === tpl.id ? { ...t, usageCount: (t.usageCount || 0) + 1, lastUsed: new Date().toISOString().split('T')[0] } : t
    );
    setTemplates(updated);
    await saveTemplates(updated);
    if (vars.length > 0) {
      setShowVarModal(true);
    } else {
      setActiveTab('compose');
    }
  };

  const applyVariables = () => {
    let subject = emailSubject;
    let body = emailBody;
    Object.entries(varValues).forEach(([varName, val]) => {
      if (val.trim()) {
        const regex = new RegExp(`\\[${varName}\\]`, 'g');
        subject = subject.replace(regex, val);
        body = body.replace(regex, val);
      }
    });
    setEmailSubject(subject);
    setEmailBody(body);
    setShowVarModal(false);
    setActiveTab('compose');
  };

  const extractVariables = (text) => {
    const matches = text.match(/\[([^\]]+)\]/g) || [];
    const unique = [...new Set(matches.map(m => m.slice(1, -1)))];
    return unique;
  };

  // ─── AI Generation ────────────────────────────────────────────────────────────
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) { Alert.alert('Enter a prompt', 'Describe the template you want.'); return; }
    setIsAIGenerating(true);
    const result = await generateAITemplate(aiPrompt, aiCategory);
    if (result) {
      const newTpl = {
        id: `tpl_ai_${Date.now()}`,
        ...result,
        category: aiCategory,
        usageCount: 0, lastUsed: 'Never', favourite: false, pinned: false,
        versions: [{ version: '1.0', date: new Date().toISOString().split('T')[0], note: 'AI Generated' }],
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0],
        tags: [...(result.tags || []), 'ai-generated'],
      };
      const updated = [newTpl, ...templates];
      setTemplates(updated);
      await saveTemplates(updated);
      setShowAIModal(false);
      setAiPrompt('');
      Alert.alert('✅ Generated!', `"${newTpl.name}" has been added to your templates.`);
    }
    setIsAIGenerating(false);
  };

  // ─── Send Email ───────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!emailSubject.trim()) { Alert.alert('Missing Subject', 'Please enter a subject.'); return; }
    if (!emailBody.trim())    { Alert.alert('Missing Body',    'Please enter a message body.'); return; }
    if (recipients.length === 0) { Alert.alert('No Recipients', 'Please add at least one recipient.'); return; }
    setIsSending(true);
    try {
      await new Promise(r => setTimeout(r, 1500)); // simulate send
      Alert.alert('✅ Sent!', `Email sent to ${recipients.length} recipient(s).`, [
        { text: 'OK', onPress: () => {
          setActiveTab('templates');
          setSelectedTemplate(null);
          setEmailSubject(''); setEmailBody('');
          setRecipients([]); setCcField(''); setBccField('');
        }},
      ]);
    } catch (_) {
      Alert.alert('Error', 'Failed to send. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // ─── Filtering & Sorting ──────────────────────────────────────────────────────
  const filteredTemplates = useMemo(() => {
    let list = [...templates];
    if (showFavourites) list = list.filter(t => t.favourite);
    if (activeCategory !== 'all') list = list.filter(t => t.category === activeCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(t =>
        String(t.name ?? '').toLowerCase().includes(q) ||
        String(t.subject ?? '').toLowerCase().includes(q) ||
        t.tags?.some(tag => tag.includes(q)) ||
        t.category.includes(q)
      );
    }
    switch (sortBy) {
      case 'recent':  list.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); break;
      case 'used':    list.sort((a, b) => b.usageCount - a.usageCount); break;
      case 'name':    list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'created': list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); break;
    }
    // Pinned first
    return [...list.filter(t => t.pinned), ...list.filter(t => !t.pinned)];
  }, [templates, activeCategory, searchQuery, sortBy, showFavourites]);

  // ─── Analytics ───────────────────────────────────────────────────────────────
  const analytics = useMemo(() => {
    const total = templates.length;
    const totalUses = templates.reduce((s, t) => s + (t.usageCount || 0), 0);
    const topUsed = [...templates].sort((a, b) => b.usageCount - a.usageCount).slice(0, 5);
    const byCat = CATEGORIES.slice(1).map(c => ({
      ...c, count: templates.filter(t => t.category === c.key).length,
      uses: templates.filter(t => t.category === c.key).reduce((s, t) => s + (t.usageCount || 0), 0),
    })).filter(c => c.count > 0);
    return { total, totalUses, favourites: templates.filter(t => t.favourite).length, topUsed, byCat };
  }, [templates]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  // getCategoryColor/Icon/Label defined at module level

  const getPreviewBody = () => {
    let body = emailBody;
    if (includeSignature) body += '\n\n---\nBest regards,\n[Your Name]\nJOBENG';
    return body;
  };

  // ─── Stable card callbacks — passed as props to TemplateCard (React.memo) ─────────────────────────────────────────────────────
  const onCardUse         = useCallback((tpl) => useTemplate(tpl), [useTemplate]);
  const onCardDuplicate   = useCallback((tpl) => duplicateTemplate(tpl), [duplicateTemplate]);
  const onCardPreview     = useCallback((tpl) => { setPreviewTemplate(tpl); setShowPreviewModal(true); }, []);
  const onCardDelete      = useCallback((id) => deleteTemplate(id), [deleteTemplate]);
  const onCardToggleFav   = useCallback((id) => toggleFavourite(id), [toggleFavourite]);
  const onCardTogglePin   = useCallback((id) => togglePinned(id), [togglePinned]);
  const onCardEdit        = useCallback((tpl) => { setEditingTemplate(tpl); setTemplateForm({ name: tpl.name, category: tpl.category, subject: tpl.subject, body: tpl.body, tags: tpl.tags?.join(', ') || '', variables: '' }); setShowCreateModal(true); }, []);
  const onCardViewHistory = useCallback((tpl) => { setPreviewTemplate(tpl); setShowVersions(true); setShowPreviewModal(true); }, []);

  // renderItemCard — stable ref; React.memo on TemplateCard skips re-renders when callbacks unchanged
  const renderItemCard = useCallback(({ item }) => (
    <TemplateCard
      tpl={item}
      onUse={onCardUse}
      onDuplicate={onCardDuplicate}
      onPreview={onCardPreview}
      onDelete={onCardDelete}
      onToggleFavourite={onCardToggleFav}
      onEdit={onCardEdit}
      onTogglePinned={onCardTogglePin}
      onViewHistory={onCardViewHistory}
    />
  ), [onCardUse, onCardDuplicate, onCardPreview, onCardDelete, onCardToggleFav, onCardEdit, onCardTogglePin, onCardViewHistory]);

  const renderTemplatesTab = () => (
    <View style={{ flex: 1, display: activeTab === 'templates' ? 'flex' : 'none' }}>
      {/* Search */}
      <View style={styles.searchRow}>
        <View style={styles.searchBox}>
          <MaterialCommunityIcons name="magnify" size={18} color={BRAND.sub} />
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search templates..."
            placeholderTextColor={BRAND.sub}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" size={18} color={BRAND.sub} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity style={styles.filterBtn} onPress={() => setShowSortFilter(true)}>
          <MaterialCommunityIcons name="sort-variant" size={22} color={BRAND.primary} />
        </TouchableOpacity>
        <TouchableOpacity style={[styles.filterBtn, showFavourites && { backgroundColor: BRAND.gold + '20' }]} onPress={() => setShowFavourites(!showFavourites)}>
          <MaterialCommunityIcons name={showFavourites ? 'star' : 'star-outline'} size={22} color={showFavourites ? BRAND.gold : BRAND.sub} />
        </TouchableOpacity>
      </View>

      {/* Category chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.catScroll} contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}>
        {CATEGORIES.map(c => (
          <TouchableOpacity
            key={c.key}
            style={[styles.catChip, activeCategory === c.key && { backgroundColor: c.color, borderColor: c.color }]}
            onPress={() => setActiveCategory(c.key)}
          >
            <MaterialCommunityIcons name={c.icon} size={14} color={activeCategory === c.key ? BRAND.white : c.color} />
            <Text style={[styles.catChipText, activeCategory === c.key && { color: BRAND.white }]}>{c.label}</Text>
            <Text style={[styles.catChipCount, activeCategory === c.key && { color: BRAND.white + 'cc' }]}>
              {c.key === 'all' ? filteredTemplates.length : templates.filter(t => t.category === c.key).length}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Action row */}
      <View style={styles.actionRow}>
        <TouchableOpacity style={styles.createBtn} onPress={() => { setEditingTemplate(null); setTemplateForm(emptyForm); setShowCreateModal(true); }}>
          <MaterialCommunityIcons name="plus" size={16} color={BRAND.white} />
          <Text style={styles.createBtnText}>New Template</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.aiBtn} onPress={() => setShowAIModal(true)}>
          <MaterialCommunityIcons name="robot-outline" size={16} color={BRAND.purple} />
          <Text style={styles.aiBtnText}>AI Generate</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.bulkBtn} onPress={() => setShowBulkSend(true)}>
          <MaterialCommunityIcons name="send-check" size={16} color={BRAND.teal} />
          <Text style={styles.bulkBtnText}>Bulk Send</Text>
        </TouchableOpacity>
      </View>

      {/* Template list */}
      <FlatList
        style={{ flex: 1 }}
        data={filteredTemplates}
        keyExtractor={t => t.id}
        renderItem={renderItemCard}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
        initialNumToRender={8}
        maxToRenderPerBatch={6}
        windowSize={5}
        removeClippedSubviews={true}
        getItemLayout={undefined}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="file-document-outline" size={56} color="#cbd5e1" />
            <Text style={styles.emptyTitle}>No templates found</Text>
            <Text style={styles.emptyText}>{searchQuery ? `No results for "${searchQuery}"` : 'Create your first template'}</Text>
            <TouchableOpacity style={styles.emptyBtn} onPress={() => { setEditingTemplate(null); setTemplateForm(emptyForm); setShowCreateModal(true); }}>
              <Text style={styles.emptyBtnText}>Create Template</Text>
            </TouchableOpacity>
          </View>
        }
      />
    </View>
  );

  // ─── Compose Tab ─────────────────────────────────────────────────────────────
  const renderComposeTab = () => (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.composeScroll} contentContainerStyle={{ padding: 16, paddingBottom: 120 }} keyboardShouldPersistTaps="handled">
        {selectedTemplate && (
          <View style={styles.tplBanner}>
            <MaterialCommunityIcons name="file-document-check" size={18} color={BRAND.primary} />
            <Text style={styles.tplBannerText}>Using: {selectedTemplate.name}</Text>
            <TouchableOpacity onPress={() => { setSelectedTemplate(null); setEmailSubject(''); setEmailBody(''); }}>
              <MaterialCommunityIcons name="close" size={18} color={BRAND.sub} />
            </TouchableOpacity>
          </View>
        )}

        {/* To field */}
        <View style={styles.composeCard}>
          <Text style={styles.composeCardTitle}>Recipients</Text>
          <View style={styles.recipientChips}>
            {recipients.map(r => (
              <View key={r.id} style={styles.recipientChip}>
                <Text style={styles.recipientChipText}>{r.name}</Text>
                <TouchableOpacity onPress={() => setRecipients(prev => prev.filter(x => x.id !== r.id))}>
                  <Ionicons name="close" size={14} color={BRAND.white} />
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={styles.addRecipientBtn} onPress={() => setActiveTab('recipients')}>
              <MaterialCommunityIcons name="account-plus" size={16} color={BRAND.primary} />
              <Text style={styles.addRecipientText}>Add Recipients</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.ccRow}>
            <Text style={styles.ccLabel}>CC</Text>
            <TextInput style={styles.ccInput} value={ccField} onChangeText={setCcField} placeholder="CC addresses" keyboardType="email-address" autoCapitalize="none" />
          </View>
          <View style={styles.ccRow}>
            <Text style={styles.ccLabel}>BCC</Text>
            <TextInput style={styles.ccInput} value={bccField} onChangeText={setBccField} placeholder="BCC addresses" keyboardType="email-address" autoCapitalize="none" />
          </View>
        </View>

        {/* Subject */}
        <View style={styles.composeCard}>
          <Text style={styles.composeCardTitle}>Subject</Text>
          <TextInput
            style={styles.subjectInput}
            value={emailSubject}
            onChangeText={setEmailSubject}
            placeholder="Email subject..."
            placeholderTextColor={BRAND.sub}
          />
        </View>

        {/* Body */}
        <View style={styles.composeCard}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text style={styles.composeCardTitle}>Message</Text>
            <TouchableOpacity style={styles.varBtn} onPress={() => {
              const vars = extractVariables(emailSubject + ' ' + emailBody);
              if (vars.length === 0) { Alert.alert('No Variables', 'No [variable] placeholders found in your email.'); return; }
              setCurrentVars(vars);
              const initVals = {};
              vars.forEach(v => { initVals[v] = varValues[v] || ''; });
              setVarValues(initVals);
              setShowVarModal(true);
            }}>
              <MaterialCommunityIcons name="code-brackets" size={16} color={BRAND.purple} />
              <Text style={styles.varBtnText}>Fill Variables</Text>
            </TouchableOpacity>
          </View>
          {/* Formatting toolbar */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.fmtBar}>
            {[
              { icon: 'format-bold',          action: () => setEmailBody(p => p + '**bold**') },
              { icon: 'format-italic',         action: () => setEmailBody(p => p + '_italic_') },
              { icon: 'format-underline',      action: () => setEmailBody(p => p + '__underline__') },
              { icon: 'format-list-bulleted',  action: () => setEmailBody(p => p + '\n• ') },
              { icon: 'format-list-numbered',  action: () => setEmailBody(p => p + '\n1. ') },
              { icon: 'link',                  action: () => setEmailBody(p => p + '[Link Text](URL)') },
            ].map((btn, i) => (
              <TouchableOpacity key={i} style={styles.fmtBtn} onPress={btn.action}>
                <MaterialCommunityIcons name={btn.icon} size={18} color={BRAND.sub} />
              </TouchableOpacity>
            ))}
          </ScrollView>
          <TextInput
            style={styles.bodyInput}
            value={emailBody}
            onChangeText={setEmailBody}
            placeholder="Write your message here..."
            placeholderTextColor={BRAND.sub}
            multiline
            textAlignVertical="top"
          />
        </View>

        {/* Variable quick-insert */}
        <View style={styles.composeCard}>
          <Text style={styles.composeCardTitle}>Quick Insert Variables</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {DEFAULT_VARIABLES.map(v => (
                <TouchableOpacity key={v} style={styles.varChip} onPress={() => setEmailBody(p => p + v)}>
                  <Text style={styles.varChipText}>{v}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>

        {/* Options */}
        <View style={styles.composeCard}>
          <Text style={styles.composeCardTitle}>Options</Text>
          {[
            { key: 'signature', label: 'Include Signature', state: includeSignature, set: setIncludeSignature },
          ].map(opt => (
            <View key={opt.key} style={styles.optRow}>
              <Text style={styles.optLabel}>{opt.label}</Text>
              <Switch value={opt.state} onValueChange={opt.set} trackColor={{ true: BRAND.primary }} thumbColor={BRAND.white} />
            </View>
          ))}
          <View style={styles.priorityRow}>
            <Text style={styles.optLabel}>Priority</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {[
                { key: 'low', label: 'Low', color: BRAND.success },
                { key: 'normal', label: 'Normal', color: BRAND.sub },
                { key: 'high', label: 'High', color: BRAND.danger },
              ].map(p => (
                <TouchableOpacity
                  key={p.key}
                  style={[styles.priorityChip, emailPriority === p.key && { backgroundColor: p.color, borderColor: p.color }]}
                  onPress={() => setEmailPriority(p.key)}
                >
                  <Text style={[styles.priorityChipText, emailPriority === p.key && { color: BRAND.white }]}>{p.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        {/* Actions */}
        <View style={styles.sendActions}>
          <TouchableOpacity style={styles.previewActionBtn} onPress={() => { setPreviewTemplate({ name: 'Preview', subject: emailSubject, body: getPreviewBody() }); setShowPreviewModal(true); }}>
            <MaterialCommunityIcons name="eye" size={16} color={BRAND.sub} />
            <Text style={styles.previewActionText}>Preview</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.saveAsBtn} onPress={() => {
            setTemplateForm({ name: selectedTemplate?.name + ' (New)' || 'New Template', category: selectedTemplate?.category || 'general', subject: emailSubject, body: emailBody, tags: '', variables: '' });
            setEditingTemplate(null);
            setShowCreateModal(true);
          }}>
            <MaterialCommunityIcons name="content-save" size={16} color={BRAND.teal} />
            <Text style={styles.saveAsBtnText}>Save Template</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.sendMainBtn, isSending && { opacity: 0.6 }]} onPress={handleSend} disabled={isSending}>
            {isSending ? <ActivityIndicator size={16} color={BRAND.white} /> : <MaterialCommunityIcons name="send" size={16} color={BRAND.white} />}
            <Text style={styles.sendMainBtnText}>{isSending ? 'Sending...' : `Send (${recipients.length})`}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ─── Recipients Tab ────────────────────────────────────────────────────────
  const recipientGroups = useMemo(() => {
    const groups = {};
    contacts.forEach(c => {
      if (!groups[c.group]) groups[c.group] = [];
      groups[c.group].push(c);
    });
    return Object.entries(groups).map(([title, data]) => ({ title, data }));
  }, [contacts]);

  const renderRecipientsTab = () => (
    <View style={{ flex: 1 }}>
      <View style={styles.searchRow}>
        <View style={styles.searchBox}>
          <MaterialCommunityIcons name="magnify" size={18} color={BRAND.sub} />
          <TextInput style={styles.searchInput} placeholder="Search contacts..." placeholderTextColor={BRAND.sub} />
        </View>
      </View>
      {recipients.length > 0 && (
        <View style={styles.selectedBanner}>
          <Text style={styles.selectedBannerText}>{recipients.length} recipient(s) selected</Text>
          <TouchableOpacity onPress={() => setRecipients([])}>
            <Text style={{ color: BRAND.danger, fontWeight: '600', fontSize: 13 }}>Clear</Text>
          </TouchableOpacity>
        </View>
      )}
      <SectionList
        sections={recipientGroups}
        keyExtractor={item => item.id}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 100 }}
        renderSectionHeader={({ section: { title } }) => (
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>{title}</Text>
            <Text style={styles.sectionHeaderCount}>{recipientGroups.find(g => g.title === title)?.data?.length}</Text>
          </View>
        )}
        renderItem={({ item }) => {
          const isSelected = recipients.find(r => r.id === item.id);
          return (
            <TouchableOpacity style={[styles.contactItem, isSelected && styles.contactItemSelected]} onPress={() => {
              setRecipients(prev =>
                prev.find(r => r.id === item.id)
                  ? prev.filter(r => r.id !== item.id)
                  : [...prev, item]
              );
            }}>
              <View style={[styles.contactAvatar, isSelected && { backgroundColor: BRAND.success }]}>
                {isSelected
                  ? <Ionicons name="checkmark" size={18} color={BRAND.white} />
                  : <Text style={styles.contactInitial}>{item.name[0]}</Text>
                }
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.contactName}>{item.name}</Text>
                <Text style={styles.contactEmail}>{item.email}</Text>
                <Text style={styles.contactCompany}>{item.company}</Text>
              </View>
              {isSelected && <MaterialCommunityIcons name="check-circle" size={20} color={BRAND.success} />}
            </TouchableOpacity>
          );
        }}
      />
      <View style={styles.recipientDone}>
        <TouchableOpacity style={styles.doneBtn} onPress={() => setActiveTab('compose')}>
          <Text style={styles.doneBtnText}>Done – {recipients.length} selected</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // ─── Analytics Tab ─────────────────────────────────────────────────────────
  const renderAnalyticsTab = () => (
    <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <Text style={styles.analyticsTitle}>Template Analytics</Text>
      <View style={styles.statsRow}>
        {[
          { label: 'Templates', value: analytics.total, icon: 'file-document-multiple', color: BRAND.primary },
          { label: 'Total Uses', value: analytics.totalUses, icon: 'send-check', color: BRAND.success },
          { label: 'Favourites', value: analytics.favourites, icon: 'star', color: BRAND.gold },
          { label: 'Categories', value: analytics.byCat.length, icon: 'view-grid', color: BRAND.teal },
        ].map(s => (
          <View key={s.label} style={[styles.statCard, { borderTopColor: s.color }]}>
            <MaterialCommunityIcons name={s.icon} size={22} color={s.color} />
            <Text style={styles.statValue}>{s.value}</Text>
            <Text style={styles.statLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.analyticsSection}>Most Used Templates</Text>
      {analytics.topUsed.map((tpl, i) => (
        <View key={tpl.id} style={styles.topUsedRow}>
          <Text style={styles.topUsedRank}>#{i + 1}</Text>
          <View style={{ flex: 1, marginHorizontal: 12 }}>
            <Text style={styles.topUsedName}>{tpl.name}</Text>
            <View style={styles.topUsedBar}>
              <View style={[styles.topUsedFill, {
                width: `${Math.min(100, (tpl.usageCount / (analytics.topUsed[0]?.usageCount || 1)) * 100)}%`,
                backgroundColor: getCategoryColor(tpl.category),
              }]} />
            </View>
          </View>
          <Text style={styles.topUsedCount}>{tpl.usageCount} uses</Text>
        </View>
      ))}

      <Text style={styles.analyticsSection}>By Category</Text>
      {analytics.byCat.map(c => (
        <View key={c.key} style={styles.catAnalyticsRow}>
          <MaterialCommunityIcons name={c.icon} size={16} color={c.color} />
          <Text style={[styles.catAnalyticsLabel, { color: c.color }]}>{c.label}</Text>
          <View style={styles.catAnalyticsBar}>
            <View style={[styles.catAnalyticsFill, { backgroundColor: c.color, width: `${Math.min(100, (c.count / analytics.total) * 100)}%` }]} />
          </View>
          <Text style={styles.catAnalyticsCount}>{c.count} templates · {c.uses} uses</Text>
        </View>
      ))}
    </ScrollView>
  );

  // ─── Modals ───────────────────────────────────────────────────────────────
  const renderCreateModal = () => (
    <Modal visible={showCreateModal} animationType="slide" presentationStyle="fullScreen" onRequestClose={() => setShowCreateModal(false)}>
      <SafeAreaView style={styles.modalBg}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <LinearGradient colors={[BRAND.primary, BRAND.teal]} style={styles.modalHeader}>
            <TouchableOpacity onPress={() => { setShowCreateModal(false); setEditingTemplate(null); setTemplateForm(emptyForm); }}>
              <Ionicons name="close" size={24} color={BRAND.white} />
            </TouchableOpacity>
            <Text style={styles.modalHeaderTitle}>{editingTemplate ? 'Edit Template' : 'New Template'}</Text>
            <TouchableOpacity onPress={createTemplate}>
              <Text style={styles.modalSaveBtn}>Save</Text>
            </TouchableOpacity>
          </LinearGradient>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Template Name *</Text>
              <TextInput style={styles.formInput} value={templateForm.name} onChangeText={v => setTemplateForm(p => ({ ...p, name: v }))} placeholder="e.g. Project Proposal" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {CATEGORIES.slice(1).map(c => (
                    <TouchableOpacity
                      key={c.key}
                      style={[styles.catChip, templateForm.category === c.key && { backgroundColor: c.color, borderColor: c.color }]}
                      onPress={() => setTemplateForm(p => ({ ...p, category: c.key }))}
                    >
                      <MaterialCommunityIcons name={c.icon} size={14} color={templateForm.category === c.key ? BRAND.white : c.color} />
                      <Text style={[styles.catChipText, templateForm.category === c.key && { color: BRAND.white }]}>{c.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Subject *</Text>
              <TextInput style={styles.formInput} value={templateForm.subject} onChangeText={v => setTemplateForm(p => ({ ...p, subject: v }))} placeholder="Email subject (use [Variable] for placeholders)" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Body *</Text>
              <TextInput
                style={[styles.formInput, { height: 240, textAlignVertical: 'top' }]}
                value={templateForm.body}
                onChangeText={v => setTemplateForm(p => ({ ...p, body: v }))}
                placeholder="Email body (use [Variable Name] for placeholders)"
                multiline
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Tags (comma separated)</Text>
              <TextInput style={styles.formInput} value={templateForm.tags} onChangeText={v => setTemplateForm(p => ({ ...p, tags: v }))} placeholder="proposal, client, engineering" />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Quick insert variables</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {DEFAULT_VARIABLES.map(v => (
                    <TouchableOpacity key={v} style={styles.varChip} onPress={() => setTemplateForm(p => ({ ...p, body: (p.body || '') + v }))}>
                      <Text style={styles.varChipText}>{v}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  const renderAIModal = () => (
    <Modal visible={showAIModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAIModal(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>AI Template Generator</Text>
          <TouchableOpacity onPress={() => setShowAIModal(false)}><Ionicons name="close" size={24} color={BRAND.dark} /></TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          <View style={[styles.aiBanner, { backgroundColor: BRAND.purple + '15' }]}>
            <MaterialCommunityIcons name="robot" size={20} color={BRAND.purple} />
            <Text style={{ flex: 1, marginLeft: 10, color: BRAND.dark, fontSize: 13 }}>
              Describe what you need and AI will create a professional email template for JOBENG.
            </Text>
          </View>
          <Text style={styles.formLabel}>Category</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {CATEGORIES.slice(1).map(c => (
                <TouchableOpacity key={c.key} style={[styles.catChip, aiCategory === c.key && { backgroundColor: c.color, borderColor: c.color }]} onPress={() => setAiCategory(c.key)}>
                  <Text style={[styles.catChipText, aiCategory === c.key && { color: BRAND.white }]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          <Text style={styles.formLabel}>Describe your template *</Text>
          <TextInput
            style={[styles.formInput, { height: 120, textAlignVertical: 'top' }]}
            value={aiPrompt}
            onChangeText={setAiPrompt}
            placeholder="e.g. A formal tender submission response for a structural engineering consultancy responding to a local council procurement..."
            multiline
          />
          <Text style={[styles.formLabel, { marginTop: 16 }]}>Quick prompts</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {[
              'Invoice overdue reminder', 'New client welcome email',
              'Project kick-off meeting invite', 'Contract award acceptance',
              'Candidate rejection with feedback', 'Quarterly newsletter to clients',
            ].map(s => (
              <TouchableOpacity key={s} style={styles.aiSuggestion} onPress={() => setAiPrompt(s)}>
                <Text style={styles.aiSuggestionText}>{s}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity
            style={[styles.aiGenBtn, isAIGenerating && { opacity: 0.6 }]}
            onPress={handleAIGenerate}
            disabled={isAIGenerating}
          >
            {isAIGenerating ? <ActivityIndicator size={18} color={BRAND.white} /> : <MaterialCommunityIcons name="auto-fix" size={18} color={BRAND.white} />}
            <Text style={styles.aiGenBtnText}>{isAIGenerating ? 'Generating...' : 'Generate Template'}</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderPreviewModal = () => (
    <Modal visible={showPreviewModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => { setShowPreviewModal(false); setShowVersions(false); }}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>{showVersions ? 'Version History' : 'Preview'}</Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            {!showVersions && previewTemplate?.id && (
              <TouchableOpacity onPress={() => setShowVersions(true)}>
                <MaterialCommunityIcons name="history" size={22} color={BRAND.primary} />
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => { setShowPreviewModal(false); setShowVersions(false); }}>
              <Ionicons name="close" size={24} color={BRAND.dark} />
            </TouchableOpacity>
          </View>
        </View>
        {previewTemplate && !showVersions && (
          <ScrollView contentContainerStyle={{ padding: 20 }}>
            <View style={styles.previewMeta}>
              <Text style={styles.previewLabel}>Template</Text>
              <Text style={styles.previewValue}>{previewTemplate.name}</Text>
            </View>
            <View style={styles.previewMeta}>
              <Text style={styles.previewLabel}>Subject</Text>
              <Text style={styles.previewValue}>{previewTemplate.subject}</Text>
            </View>
            {previewTemplate.category && (
              <View style={[styles.catBadge, { backgroundColor: getCategoryColor(previewTemplate.category) + '20', alignSelf: 'flex-start', marginBottom: 16 }]}>
                <Text style={[styles.catBadgeText, { color: getCategoryColor(previewTemplate.category) }]}>{getCategoryLabel(previewTemplate.category)}</Text>
              </View>
            )}
            <View style={styles.previewBody}>
              <Text style={styles.previewBodyText}>{previewTemplate.body}</Text>
            </View>
            {previewTemplate.variables?.length > 0 && (
              <View style={{ marginTop: 16 }}>
                <Text style={styles.previewLabel}>Variables ({previewTemplate.variables.length})</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {previewTemplate.variables.map(v => (
                    <View key={v} style={styles.varTagChip}><Text style={styles.varTagText}>[{v}]</Text></View>
                  ))}
                </View>
              </View>
            )}
            <View style={styles.previewActions}>
              <TouchableOpacity style={styles.previewUseBtn} onPress={() => { setShowPreviewModal(false); useTemplate(previewTemplate); }}>
                <MaterialCommunityIcons name="pencil" size={16} color={BRAND.white} />
                <Text style={styles.previewUseBtnText}>Use This Template</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}
        {previewTemplate && showVersions && (
          <ScrollView contentContainerStyle={{ padding: 20 }}>
            {(previewTemplate.versions || []).reverse().map((v, i) => (
              <View key={i} style={styles.versionItem}>
                <View style={styles.versionBadge}>
                  <Text style={styles.versionBadgeText}>v{v.version}</Text>
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={styles.versionDate}>{v.date}</Text>
                  <Text style={styles.versionNote}>{v.note}</Text>
                </View>
              </View>
            ))}
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderVarModal = () => (
    <Modal visible={showVarModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowVarModal(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Fill Variables</Text>
          <TouchableOpacity onPress={() => setShowVarModal(false)}><Ionicons name="close" size={24} color={BRAND.dark} /></TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
          <Text style={{ color: BRAND.sub, marginBottom: 20, fontSize: 14 }}>
            Fill in the variable values below to personalise your email before composing.
          </Text>
          {currentVars.map(varName => (
            <View key={varName} style={styles.formGroup}>
              <Text style={styles.formLabel}>{varName}</Text>
              <TextInput
                style={styles.formInput}
                value={varValues[varName] || ''}
                onChangeText={v => setVarValues(p => ({ ...p, [varName]: v }))}
                placeholder={`Enter ${varName}...`}
              />
            </View>
          ))}
          <TouchableOpacity style={styles.applyVarsBtn} onPress={applyVariables}>
            <MaterialCommunityIcons name="check" size={18} color={BRAND.white} />
            <Text style={styles.applyVarsBtnText}>Apply & Continue</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.skipVarsBtn} onPress={() => { setShowVarModal(false); setActiveTab('compose'); }}>
            <Text style={styles.skipVarsBtnText}>Skip – fill later</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderBulkSendModal = () => (
    <Modal visible={showBulkSend} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowBulkSend(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Bulk Send Campaign</Text>
          <TouchableOpacity onPress={() => setShowBulkSend(false)}><Ionicons name="close" size={24} color={BRAND.dark} /></TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
          <View style={styles.aiBanner}>
            <MaterialCommunityIcons name="information-outline" size={18} color={BRAND.blue} />
            <Text style={{ flex: 1, marginLeft: 10, color: BRAND.dark, fontSize: 13 }}>
              Select a template and recipients below to send a personalised campaign to multiple contacts.
            </Text>
          </View>
          <Text style={styles.analyticsSection}>Select Template</Text>
          {templates.slice(0, 5).map(tpl => (
            <TouchableOpacity
              key={tpl.id}
              style={[styles.contactItem, selectedTemplate?.id === tpl.id && styles.contactItemSelected]}
              onPress={() => setSelectedTemplate(tpl)}
            >
              <View style={[styles.catIconBadge, { backgroundColor: getCategoryColor(tpl.category) + '20' }]}>
                <MaterialCommunityIcons name={getCategoryIcon(tpl.category)} size={16} color={getCategoryColor(tpl.category)} />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.contactName}>{tpl.name}</Text>
                <Text style={styles.contactEmail}>{tpl.subject}</Text>
              </View>
              {selectedTemplate?.id === tpl.id && <MaterialCommunityIcons name="check-circle" size={20} color={BRAND.success} />}
            </TouchableOpacity>
          ))}
          <Text style={styles.analyticsSection}>Recipients ({recipients.length} selected)</Text>
          <TouchableOpacity style={styles.addRecipientBtn} onPress={() => { setShowBulkSend(false); setActiveTab('recipients'); }}>
            <MaterialCommunityIcons name="account-plus" size={16} color={BRAND.primary} />
            <Text style={styles.addRecipientText}>Manage Recipients</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.aiGenBtn, { marginTop: 24 }]}
            onPress={() => {
              if (!selectedTemplate) { Alert.alert('Select Template', 'Please select a template.'); return; }
              if (recipients.length === 0) { Alert.alert('No Recipients', 'Please select recipients.'); return; }
              setShowBulkSend(false);
              Alert.alert('Campaign Sent!', `${recipients.length} emails sent using "${selectedTemplate.name}".`);
            }}
          >
            <MaterialCommunityIcons name="send-check" size={18} color={BRAND.white} />
            <Text style={styles.aiGenBtnText}>Send Campaign ({recipients.length} emails)</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderSortModal = () => (
    <Modal visible={showSortFilter} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowSortFilter(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Sort Templates</Text>
          <TouchableOpacity onPress={() => setShowSortFilter(false)}><Ionicons name="close" size={24} color={BRAND.dark} /></TouchableOpacity>
        </View>
        <View style={{ padding: 20 }}>
          {[
            { key: 'recent',  label: 'Recently Updated', icon: 'clock-outline' },
            { key: 'used',    label: 'Most Used',         icon: 'trending-up' },
            { key: 'name',    label: 'Name (A–Z)',        icon: 'sort-alphabetical-ascending' },
            { key: 'created', label: 'Recently Created',  icon: 'calendar-plus' },
          ].map(s => (
            <TouchableOpacity key={s.key} style={[styles.sortItem, sortBy === s.key && styles.sortItemActive]} onPress={() => { setSortBy(s.key); setShowSortFilter(false); }}>
              <MaterialCommunityIcons name={s.icon} size={20} color={sortBy === s.key ? BRAND.primary : BRAND.sub} />
              <Text style={[styles.sortItemText, sortBy === s.key && { color: BRAND.primary }]}>{s.label}</Text>
              {sortBy === s.key && <MaterialCommunityIcons name="check" size={18} color={BRAND.primary} />}
            </TouchableOpacity>
          ))}
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ─── Main Render ─────────────────────────────────────────────────────────────
  const TABS_CONFIG = [
    { key: 'templates',  label: 'Templates',  icon: 'file-document-multiple' },
    { key: 'compose',    label: 'Compose',    icon: 'pencil' },
    { key: 'recipients', label: 'Recipients', icon: 'account-group' },
    { key: 'analytics',  label: 'Analytics',  icon: 'chart-bar' },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.primary} />

      {/* Header */}
      <LinearGradient colors={[BRAND.primary, BRAND.teal]} style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={BRAND.white} />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={styles.headerTitle}>Email Templates</Text>
          <Text style={styles.headerSub}>{templates.length} templates · {templates.reduce((s, t) => s + (t.usageCount || 0), 0)} total uses</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerBtn} onPress={() => { setEditingTemplate(null); setTemplateForm(emptyForm); setShowCreateModal(true); }}>
            <MaterialCommunityIcons name="plus" size={24} color={BRAND.white} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowAIModal(true)}>
            <MaterialCommunityIcons name="robot-outline" size={24} color={BRAND.white} />
          </TouchableOpacity>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={{ width: 32, height: 32, borderRadius: 16, marginLeft: 6, resizeMode: 'cover' }} />
          ) : (
            <View style={{ width: 32, height: 32, borderRadius: 16, marginLeft: 6, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: BRAND.white, fontWeight: 'bold', fontSize: 11 }}>{companyInitials}</Text>
            </View>
          )}
        </View>
      </LinearGradient>

      {/* Tab bar */}
      <View style={styles.tabBar}>
        {TABS_CONFIG.map(tab => {
          const active = activeTab === tab.key;
          return (
            <TouchableOpacity key={tab.key} style={[styles.tabItem, active && styles.tabItemActive]} onPress={() => setActiveTab(tab.key)}>
              <MaterialCommunityIcons name={tab.icon} size={18} color={active ? BRAND.primary : BRAND.sub} />
              <Text style={[styles.tabLabel, active && styles.tabLabelActive]}>{tab.label}</Text>
              {tab.key === 'recipients' && recipients.length > 0 && (
                <View style={styles.tabBadge}><Text style={styles.tabBadgeText}>{recipients.length}</Text></View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content */}
      <View style={{ flex: 1 }}>
        {renderTemplatesTab()}
        {activeTab === 'compose'    && renderComposeTab()}
        {activeTab === 'recipients' && renderRecipientsTab()}
        {activeTab === 'analytics'  && renderAnalyticsTab()}
      </View>

      {/* Modals — interactive modals always mounted (visible prop controls display)
           read-only/lightweight modals gated to avoid rendering cost */}
      {renderCreateModal()}
      {renderAIModal()}
      {showPreviewModal && renderPreviewModal()}
      {showVarModal     && renderVarModal()}
      {showBulkSend     && renderBulkSendModal()}
      {showSortFilter   && renderSortModal()}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: BRAND.bg },
  header:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, paddingTop: Platform.OS === 'ios' ? 54 : 14 },
  backBtn:        { marginRight: 4, padding: 4 },
  headerTitle:    { fontSize: 17, fontWeight: '700', color: BRAND.white },
  headerSub:      { fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 1 },
  headerActions:  { flexDirection: 'row', gap: 4 },
  headerBtn:      { padding: 8 },
  tabBar:         { flexDirection: 'row', backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  tabItem:        { flex: 1, flexDirection: 'column', alignItems: 'center', paddingVertical: 10, gap: 3 },
  tabItemActive:  { borderBottomWidth: 2.5, borderBottomColor: BRAND.primary },
  tabLabel:       { fontSize: 11, color: BRAND.sub, fontWeight: '500' },
  tabLabelActive: { color: BRAND.primary, fontWeight: '700' },
  tabBadge:       { position: 'absolute', top: 4, right: 8, minWidth: 16, height: 16, borderRadius: 8, backgroundColor: BRAND.danger, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  tabBadgeText:   { fontSize: 10, color: BRAND.white, fontWeight: '700' },
  // Search / filter row
  searchRow:      { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, gap: 8, backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  searchBox:      { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.bg, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8 },
  searchInput:    { flex: 1, fontSize: 14, color: BRAND.dark, marginHorizontal: 8 },
  filterBtn:      { width: 40, height: 40, borderRadius: 10, backgroundColor: BRAND.bg, justifyContent: 'center', alignItems: 'center' },
  // Category scroll
  catScroll:      { maxHeight: 52, backgroundColor: BRAND.white, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  catChip:        { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.bg },
  catChipText:    { fontSize: 12, fontWeight: '600', color: BRAND.sub },
  catChipCount:   { fontSize: 11, color: BRAND.sub },
  // Action row
  actionRow:      { flexDirection: 'row', padding: 12, gap: 8, backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  createBtn:      { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: BRAND.primary, paddingVertical: 10, borderRadius: 10 },
  createBtnText:  { color: BRAND.white, fontSize: 13, fontWeight: '700' },
  aiBtn:          { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: BRAND.purple + '15', paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: BRAND.purple + '40' },
  aiBtnText:      { color: BRAND.purple, fontSize: 13, fontWeight: '700' },
  bulkBtn:        { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: BRAND.teal + '15', paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: BRAND.teal + '40' },
  bulkBtnText:    { color: BRAND.teal, fontSize: 13, fontWeight: '700' },
  // Template card
  tplCard:        { backgroundColor: BRAND.white, borderRadius: 14, padding: 16, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.07, shadowRadius: 4, elevation: 2 },
  tplCardPinned:  { borderWidth: 1.5, borderColor: BRAND.primary + '60' },
  tplCardTop:     { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
  catIconBadge:   { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  tplCardName:    { fontSize: 15, fontWeight: '700', color: BRAND.dark },
  tplCardCat:     { fontSize: 12, color: BRAND.sub, marginTop: 2 },
  tplCardSubject: { fontSize: 13, color: BRAND.primary, fontWeight: '500', marginBottom: 6 },
  tplCardPreview: { fontSize: 13, color: BRAND.sub, lineHeight: 19, marginBottom: 10 },
  tplCardFooter:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  tagChip:        { backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  tagChipText:    { fontSize: 11, color: BRAND.sub },
  tplMeta:        { fontSize: 11, color: BRAND.sub },
  tplActions:     { flexDirection: 'row', borderTopWidth: 1, borderTopColor: BRAND.line, paddingTop: 12 },
  tplActionBtn:   { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 },
  tplActionText:  { fontSize: 13, color: BRAND.sub, fontWeight: '500' },
  tplActionDiv:   { width: 1, height: 20, backgroundColor: BRAND.line },
  // Empty
  emptyState:     { alignItems: 'center', justifyContent: 'center', paddingVertical: 80 },
  emptyTitle:     { fontSize: 18, fontWeight: '700', color: BRAND.dark, marginTop: 16 },
  emptyText:      { fontSize: 14, color: BRAND.sub, marginTop: 8, marginBottom: 24, textAlign: 'center' },
  emptyBtn:       { backgroundColor: BRAND.primary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 24 },
  emptyBtnText:   { color: BRAND.white, fontWeight: '700', fontSize: 14 },
  // Compose
  composeScroll:  { flex: 1 },
  tplBanner:      { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.primary + '15', borderRadius: 10, padding: 12, marginBottom: 12, gap: 10 },
  tplBannerText:  { flex: 1, fontSize: 13, fontWeight: '600', color: BRAND.primary },
  composeCard:    { backgroundColor: BRAND.white, borderRadius: 12, padding: 16, marginBottom: 10 },
  composeCardTitle:{ fontSize: 14, fontWeight: '700', color: BRAND.dark, marginBottom: 12 },
  recipientChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  recipientChip:  { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: BRAND.primary, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 },
  recipientChipText:{ color: BRAND.white, fontSize: 13, fontWeight: '500' },
  addRecipientBtn:{ flexDirection: 'row', alignItems: 'center', gap: 6 },
  addRecipientText:{ color: BRAND.primary, fontSize: 14, fontWeight: '600' },
  ccRow:          { flexDirection: 'row', alignItems: 'center', borderTopWidth: 1, borderTopColor: BRAND.line, paddingTop: 10, marginTop: 6 },
  ccLabel:        { width: 36, fontSize: 13, fontWeight: '600', color: BRAND.sub },
  ccInput:        { flex: 1, fontSize: 14, color: BRAND.dark, marginLeft: 8 },
  subjectInput:   { fontSize: 15, color: BRAND.dark, borderWidth: 1, borderColor: BRAND.line, borderRadius: 8, padding: 12 },
  varBtn:         { flexDirection: 'row', alignItems: 'center', gap: 5 },
  varBtnText:     { fontSize: 12, color: BRAND.purple, fontWeight: '600' },
  fmtBar:         { marginBottom: 10 },
  fmtBtn:         { width: 36, height: 36, borderRadius: 8, backgroundColor: BRAND.bg, justifyContent: 'center', alignItems: 'center', marginRight: 6 },
  bodyInput:      { minHeight: 220, fontSize: 15, color: BRAND.dark, textAlignVertical: 'top', lineHeight: 24, borderWidth: 1, borderColor: BRAND.line, borderRadius: 8, padding: 12 },
  varChip:        { backgroundColor: BRAND.primary + '15', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16 },
  varChipText:    { fontSize: 12, color: BRAND.primary, fontWeight: '500' },
  optRow:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  optLabel:       { fontSize: 15, color: BRAND.dark },
  priorityRow:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderTopWidth: 1, borderTopColor: BRAND.line, marginTop: 8 },
  priorityChip:   { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.bg },
  priorityChipText:{ fontSize: 12, fontWeight: '600', color: BRAND.dark },
  sendActions:    { flexDirection: 'row', gap: 8, marginTop: 8 },
  previewActionBtn:{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 13, borderRadius: 10, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.white },
  previewActionText:{ fontSize: 13, color: BRAND.sub, fontWeight: '600' },
  saveAsBtn:      { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 13, borderRadius: 10, borderWidth: 1, borderColor: BRAND.teal + '60', backgroundColor: BRAND.teal + '10' },
  saveAsBtnText:  { fontSize: 13, color: BRAND.teal, fontWeight: '600' },
  sendMainBtn:    { flex: 2, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 13, borderRadius: 10, backgroundColor: BRAND.primary },
  sendMainBtnText:{ fontSize: 14, fontWeight: '700', color: BRAND.white },
  // Recipients
  selectedBanner: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, backgroundColor: BRAND.primary + '10' },
  selectedBannerText:{ fontSize: 13, fontWeight: '600', color: BRAND.primary },
  sectionHeader:  { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, marginTop: 8 },
  sectionHeaderText:{ flex: 1, fontSize: 13, fontWeight: '700', color: BRAND.sub, textTransform: 'uppercase', letterSpacing: 0.5 },
  sectionHeaderCount:{ fontSize: 12, color: BRAND.sub },
  contactItem:    { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, borderRadius: 10, padding: 14, marginBottom: 6 },
  contactItemSelected:{ backgroundColor: '#eff6ff', borderWidth: 1.5, borderColor: BRAND.primary + '40' },
  contactAvatar:  { width: 42, height: 42, borderRadius: 21, backgroundColor: BRAND.primary, justifyContent: 'center', alignItems: 'center' },
  contactInitial: { color: BRAND.white, fontSize: 16, fontWeight: '700' },
  contactName:    { fontSize: 14, fontWeight: '600', color: BRAND.dark },
  contactEmail:   { fontSize: 12, color: BRAND.primary, marginTop: 1 },
  contactCompany: { fontSize: 12, color: BRAND.sub, marginTop: 1 },
  recipientDone:  { padding: 16, borderTopWidth: 1, borderTopColor: BRAND.line, backgroundColor: BRAND.white },
  doneBtn:        { backgroundColor: BRAND.primary, paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  doneBtnText:    { color: BRAND.white, fontSize: 15, fontWeight: '700' },
  // Analytics
  analyticsTitle: { fontSize: 20, fontWeight: '800', color: BRAND.dark, marginBottom: 16 },
  analyticsSection:{ fontSize: 15, fontWeight: '700', color: BRAND.dark, marginBottom: 12, marginTop: 20 },
  statsRow:       { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 8 },
  statCard:       { width: (width - 52) / 2, backgroundColor: BRAND.white, borderRadius: 12, padding: 14, alignItems: 'center', borderTopWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  statValue:      { fontSize: 26, fontWeight: '800', color: BRAND.dark, marginTop: 6 },
  statLabel:      { fontSize: 12, color: BRAND.sub, marginTop: 4, textAlign: 'center' },
  topUsedRow:     { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  topUsedRank:    { width: 26, fontSize: 14, fontWeight: '700', color: BRAND.sub },
  topUsedName:    { fontSize: 14, fontWeight: '600', color: BRAND.dark, marginBottom: 5 },
  topUsedBar:     { height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' },
  topUsedFill:    { height: '100%', borderRadius: 3 },
  topUsedCount:   { width: 60, fontSize: 12, color: BRAND.sub, textAlign: 'right' },
  catAnalyticsRow:{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 8 },
  catAnalyticsLabel:{ width: 70, fontSize: 12, fontWeight: '600' },
  catAnalyticsBar:{ flex: 1, height: 8, backgroundColor: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
  catAnalyticsFill:{ height: '100%', borderRadius: 4 },
  catAnalyticsCount:{ width: 120, fontSize: 11, color: BRAND.sub, textAlign: 'right' },
  // Modal shared
  modalBg:        { flex: 1, backgroundColor: BRAND.bg },
  modalHeader:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, paddingTop: Platform.OS === 'ios' ? 54 : 14 },
  modalHeaderTitle:{ fontSize: 16, fontWeight: '700', color: BRAND.white },
  modalSaveBtn:   { fontSize: 15, fontWeight: '700', color: BRAND.gold },
  sheetHeader:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16, backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  sheetTitle:     { fontSize: 18, fontWeight: '700', color: BRAND.dark },
  // Form
  formGroup:      { marginBottom: 18 },
  formLabel:      { fontSize: 13, fontWeight: '700', color: BRAND.dark, marginBottom: 8 },
  formInput:      { borderWidth: 1, borderColor: BRAND.line, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: BRAND.dark, backgroundColor: BRAND.white },
  // AI
  aiBanner:       { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: BRAND.blue + '15', borderRadius: 10, padding: 14, marginBottom: 20 },
  aiSuggestion:   { backgroundColor: BRAND.purple + '15', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16 },
  aiSuggestionText:{ fontSize: 12, color: BRAND.purple, fontWeight: '600' },
  aiGenBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: BRAND.primary, paddingVertical: 14, borderRadius: 12 },
  aiGenBtnText:   { color: BRAND.white, fontSize: 15, fontWeight: '700' },
  // Preview modal
  previewMeta:    { marginBottom: 12 },
  previewLabel:   { fontSize: 12, fontWeight: '700', color: BRAND.sub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  previewValue:   { fontSize: 16, color: BRAND.dark, fontWeight: '500' },
  catBadge:       { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 12 },
  catBadgeText:   { fontSize: 12, fontWeight: '700' },
  previewBody:    { backgroundColor: BRAND.white, borderRadius: 12, padding: 16, marginBottom: 16 },
  previewBodyText:{ fontSize: 14, color: BRAND.dark, lineHeight: 22 },
  varTagChip:     { backgroundColor: BRAND.purple + '15', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 },
  varTagText:     { fontSize: 12, color: BRAND.purple, fontWeight: '500' },
  previewActions: { marginTop: 20 },
  previewUseBtn:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: BRAND.primary, paddingVertical: 14, borderRadius: 12 },
  previewUseBtnText:{ color: BRAND.white, fontSize: 15, fontWeight: '700' },
  // Version history
  versionItem:    { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  versionBadge:   { backgroundColor: BRAND.primary, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  versionBadgeText:{ color: BRAND.white, fontSize: 13, fontWeight: '700' },
  versionDate:    { fontSize: 13, fontWeight: '600', color: BRAND.dark },
  versionNote:    { fontSize: 13, color: BRAND.sub, marginTop: 3 },
  // Variable modal
  applyVarsBtn:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: BRAND.primary, paddingVertical: 14, borderRadius: 12, marginTop: 20 },
  applyVarsBtnText:{ color: BRAND.white, fontSize: 15, fontWeight: '700' },
  skipVarsBtn:    { alignItems: 'center', marginTop: 12 },
  skipVarsBtnText:{ color: BRAND.sub, fontSize: 14, fontWeight: '500' },
  // Sort modal
  sortItem:       { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14, paddingHorizontal: 14, borderRadius: 10, marginBottom: 6, backgroundColor: BRAND.white },
  sortItemActive: { backgroundColor: '#eff6ff' },
  sortItemText:   { flex: 1, fontSize: 15, color: BRAND.dark },
});

const StatusBar = require('react-native').StatusBar;